import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Landing from "./pages/Landing";
import AuthCallback from "./pages/AuthCallback";
import ProfileSelector from "./pages/ProfileSelector";
import StreamingHub from "./pages/StreamingHub";
import ChoosePlan from "./pages/ChoosePlan";
import Games from "./pages/Games";
import Marketplace from "./pages/Marketplace";
import SocialFeed from "./pages/SocialFeed";
import UserProfile from "./pages/UserProfile";
import MyProfile from "./pages/MyProfile";
import Messages from "./pages/Messages";
import Childcare from "./pages/Childcare";
import ParentDashboard from "./pages/ParentDashboard";
import PaymentSuccess from "./pages/PaymentSuccess";
import AdminPortal from "./pages/AdminPortal";
import { BubzLogo } from "./components/BubzLogo";
import InstallPrompt from "./components/InstallPrompt";
import SiteFooter from "./components/SiteFooter";
import Legal from "./pages/Legal";
import InviteAccept from "./pages/InviteAccept";
import TermsNotice from "./components/TermsNotice";

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FFFDF7]">
      <div className="text-center animate-floaty">
        <BubzLogo size="xl" />
        <p className="mt-3 text-slate-500">Loading the fun…</p>
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullScreenLoader />;
  if (user === false) return <Navigate to="/" replace />;
  return children;
}

// Adults-only zones (Marketplace, Family Feed, Parent Zone). Kids are blocked.
function RequireParent({ children }) {
  const { activeKid } = useAuth();
  if (!activeKid) return <Navigate to="/profiles" replace />;
  if (!activeKid.parent) return <Navigate to="/watch" replace />;
  return children;
}

// Kid-only zone (Games). A parent must switch to a kid profile.
function RequireKid({ children }) {
  const { activeKid } = useAuth();
  if (!activeKid) return <Navigate to="/profiles" replace />;
  if (activeKid.parent) return <Navigate to="/profiles" replace />;
  return children;
}

// Streaming requires an active Bubz + subscription (Demo tier is free).
function RequireSubscription({ children }) {
  const { subscription } = useAuth();
  if (subscription === undefined) return <FullScreenLoader />;
  if (!subscription) return <Navigate to="/plans" replace />;
  return children;
}

// Admin portal — admin role only.
function RequireAdmin({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullScreenLoader />;
  if (user === false) return <Navigate to="/" replace />;
  if (!user.is_admin) return <Navigate to="/profiles" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/terms" element={<Legal type="terms" />} />
      <Route path="/privacy" element={<Legal type="privacy" />} />
      <Route path="/invite/:token" element={<InviteAccept />} />
      <Route path="/plans" element={<Protected><ChoosePlan /></Protected>} />
      <Route path="/profiles" element={<Protected><ProfileSelector /></Protected>} />
      <Route path="/watch" element={<Protected><RequireSubscription><StreamingHub /></RequireSubscription></Protected>} />
      <Route path="/games" element={<Protected><RequireKid><Games /></RequireKid></Protected>} />
      <Route path="/marketplace" element={<Protected><RequireParent><Marketplace /></RequireParent></Protected>} />
      <Route path="/social" element={<Protected><RequireParent><SocialFeed /></RequireParent></Protected>} />
      <Route path="/social/profile" element={<Protected><RequireParent><MyProfile /></RequireParent></Protected>} />
      <Route path="/social/u/:id" element={<Protected><RequireParent><UserProfile /></RequireParent></Protected>} />
      <Route path="/messages" element={<Protected><RequireParent><Messages /></RequireParent></Protected>} />
      <Route path="/childcare" element={<Protected><RequireParent><Childcare /></RequireParent></Protected>} />
      <Route path="/parent" element={<Protected><RequireParent><ParentDashboard /></RequireParent></Protected>} />
      <Route path="/admin" element={<Protected><RequireAdmin><AdminPortal /></RequireAdmin></Protected>} />
      <Route path="/payment/success" element={<Protected><RequireParent><PaymentSuccess /></RequireParent></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors />
          <InstallPrompt />
          <AppRoutes />
          <TermsNotice />
          <SiteFooter />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
